import React from "react"
import browserStorageService, {
	localStorageService,
	USER_OPERATOR_KEY,
} from "services/browserStorage"

import { BICYCODE_ID } from "operators/bicycode/config"
import { DECATHLON_ID } from "operators/decathlon/config"
import { MFC_ID } from "operators/mfc/config"
import { isNotEmpty, OperatorInterface, operators, operatorsFolderName } from "utils"

type OperatorContextInterface = {
	currentOperator: OperatorInterface
	isOperatorComplete: boolean
	isBicycode: boolean
	isDecathlon: boolean
	isMFC: boolean
	setCurrentOperator: React.Dispatch<OperatorInterface>
}

const OperatorContext = React.createContext({} as OperatorContextInterface)

const useOperator = () => React.useContext(OperatorContext)

/**********************
 ** Context provider **
 **********************/

const OperatorProvider = ({ children }: { children: React.ReactNode }) => {
	const [currentOperator, setCurrentOperator] = React.useState<OperatorInterface>()
	const [isOperatorComplete, setIsOperatorComplete] = React.useState<boolean>(false)

	const isBicycode = currentOperator?.id === BICYCODE_ID
	const isDecathlon = currentOperator?.id === DECATHLON_ID
	const isMFC = currentOperator?.id === MFC_ID

	React.useEffect(() => {
		const storageUserOperator = browserStorageService.get(USER_OPERATOR_KEY, {
			priority: "sessionStorage",
		})

		let getCurrentOperator

		if (process.env.REACT_APP_MODE === "development") {
			// We check if url contains one of the operators (ex: "/bicycode")
			// First, split url in array of strings
			const splittedPathname = document.location.pathname.split("/")

			// Then find operator
			const getOperator = splittedPathname.find((word) => operatorsFolderName.includes(word))

			if (isNotEmpty(getOperator)) {
				// Clear localStorage to avoid old data connexion
				localStorageService.clearSession()
				getCurrentOperator = operators.find(
					(operator) => operator.subDomain === getOperator
				)
				setCurrentOperator(getCurrentOperator)
				browserStorageService.set(USER_OPERATOR_KEY, getCurrentOperator.subDomain, {
					target: "sessionStorage",
					priority: "sessionStorage",
				})

				// Warning, we have some special cases like :
				// - /obike_owner/decathlon/s-inscrire
				// - /obike_owner/decathlon/modifier-mon-velo?locale=fr-FR&token=eyJhb
				// We don't want to redirect user to the homepage but the specific page without the folderName
				// So we have to compare the last string in the splittedPathname array
				const getLastItemFromSplittedPathname = splittedPathname.splice(-1, 1)
				const isLastItemIsOperatorFolderName =
					getLastItemFromSplittedPathname[0] === getOperator

				const newUrlToRedirect = `/${process.env.REACT_APP_BASENAME}/${
					isLastItemIsOperatorFolderName ? "" : getLastItemFromSplittedPathname
				}${document.location?.search}`
				// Redirect to signin or specific page
				window.location.replace(newUrlToRedirect)

				setIsOperatorComplete(true)
			} else if (isNotEmpty(storageUserOperator)) {
				// Or, we check the localStorage
				const getCurrentOperator = operators.find(
					(operator) => operator.subDomain === storageUserOperator
				)
				if (isNotEmpty(getCurrentOperator)) setCurrentOperator(getCurrentOperator)
				setIsOperatorComplete(true)
			}
		} else {
			getCurrentOperator = operators.find(
				(operator) => operator.url === document.location.host
			)
			setCurrentOperator(getCurrentOperator)
			browserStorageService.set(USER_OPERATOR_KEY, getCurrentOperator.subDomain, {
				target: "sessionStorage",
				priority: "sessionStorage",
			})
			setIsOperatorComplete(true)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	React.useEffect(() => {
		if (isNotEmpty(currentOperator)) {
			for (const [key, value] of Object.entries(currentOperator?.theme)) {
				document.documentElement.style.setProperty(`--${key}`, value)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOperator])

	return (
		<OperatorContext.Provider
			value={{
				currentOperator,
				setCurrentOperator,
				isOperatorComplete,
				isBicycode,
				isDecathlon,
				isMFC,
			}}
		>
			{children}
		</OperatorContext.Provider>
	)
}

export { useOperator, OperatorProvider }
